/**
 * Local Storage Service using IndexedDB
 * 
 * Provides browser-only storage for notes without requiring server authentication.
 * Implements the Local-Only Mode as specified in the comprehensive architecture.
 * 
 * @author SFG Java Developers
 * @version 1.0.0
 */

class LocalStorageService {
  constructor() {
    this.dbName = 'NotesAppLocal'
    this.version = 3  // Incremented to add image_cache store
    this.db = null
    this.sessionId = null
  }

  /**
   * Encrypt sensitive note content using AES-GCM
   */
  async encryptContent(content, password) {
    if (!content || !password) return content
    
    try {
      const { encryptText } = await import('../utils/crypto')
      return await encryptText(content, password)
    } catch (error) {
      console.error('Encryption failed:', error)
      return content
    }
  }


  /**
   * Decrypt sensitive note content using AES-GCM
   */
  async decryptContent(encryptedContent, password) {
    if (!encryptedContent || !password) return encryptedContent
    
    try {
      const { decryptText } = await import('../utils/crypto')
      return await decryptText(encryptedContent, password)
    } catch (error) {
      console.error('Decryption failed:', error)
      return encryptedContent
    }
  }


  /**
   * Initialize IndexedDB for local storage
   */
  async initialize() {
    try {
      this.db = await this.openDatabase()
      this.sessionId = this.getOrCreateSessionId()
      console.log('📦 Local IndexedDB initialized:', this.sessionId)
      return this.db
    } catch (error) {
      console.error('❌ Failed to initialize local storage:', error)
      throw error
    }
  }

  /**
   * Open IndexedDB database
   */
  openDatabase() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version)
      
      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve(request.result)
      
      request.onupgradeneeded = (event) => {
        const db = event.target.result
        this.createLocalStores(db)
      }
    })
  }

  /**
   * Create object stores for local data
   */
  createLocalStores(db) {
    const oldVersion = db.oldVersion || 0
    
    // Notes store
    if (!db.objectStoreNames.contains('local_notes')) {
      const notesStore = db.createObjectStore('local_notes', { keyPath: 'noteId' })
      notesStore.createIndex('created', 'createdAt')
      notesStore.createIndex('modified', 'lastModified')
      notesStore.createIndex('title', 'title')
      notesStore.createIndex('folderId', 'folderId')
    }

    // Folders store
    if (!db.objectStoreNames.contains('local_folders')) {
      const foldersStore = db.createObjectStore('local_folders', { keyPath: 'folderId' })
      foldersStore.createIndex('name', 'name')
      foldersStore.createIndex('created', 'createdAt')
    }

    // Attachments store (metadata only)
    if (!db.objectStoreNames.contains('local_attachments')) {
      const attachmentsStore = db.createObjectStore('local_attachments', { keyPath: 'id' })
      attachmentsStore.createIndex('noteId', 'noteId')
      attachmentsStore.createIndex('fileType', 'fileType')
    }

    // Files store (actual file blobs) - NEW in version 2
    if (!db.objectStoreNames.contains('local_files')) {
      console.log('📁 Creating local_files store for file-based image storage')
      const filesStore = db.createObjectStore('local_files', { keyPath: 'id' })
      filesStore.createIndex('path', 'path')
    }

    // Metadata store
    if (!db.objectStoreNames.contains('local_metadata')) {
      const metadataStore = db.createObjectStore('local_metadata', { keyPath: 'key' })
    }

    // Usage tracking store
    if (!db.objectStoreNames.contains('local_usage')) {
      const usageStore = db.createObjectStore('local_usage', { keyPath: 'date' })
    }

    // Image cache store (base64 images by Google Drive file ID) - NEW in version 3
    if (!db.objectStoreNames.contains('image_cache')) {
      console.log('📁 Creating image_cache store for base64 image caching')
      const imageCacheStore = db.createObjectStore('image_cache', { keyPath: 'fileId' })
      imageCacheStore.createIndex('fileName', 'fileName')
      imageCacheStore.createIndex('cachedAt', 'cachedAt')
    }

    console.log(`📁 Created local storage schema (upgraded from version ${oldVersion} to ${this.version})`)
  }

  /**
   * Get or create session ID
   */
  getOrCreateSessionId() {
    let sessionId = localStorage.getItem('localSessionId')
    if (!sessionId) {
      sessionId = 'local-' + crypto.randomUUID()
      localStorage.setItem('localSessionId', sessionId)
    }
    return sessionId
  }

  /**
   * Save note to local storage
   */
  async saveNote(note, folderId = null, password = null) {
    if (!this.db) await this.initialize()

    const transaction = this.db.transaction(['local_notes', 'local_folders'], 'readwrite')
    const notesStore = transaction.objectStore('local_notes')
    const foldersStore = transaction.objectStore('local_folders')
    
    // Determine if this is a sensitive note by checking folder type
    let isSensitive = false
    const targetFolderId = note.folderId || folderId
    
    if (targetFolderId) {
      try {
        const folder = await new Promise((resolve, reject) => {
          const folderRequest = foldersStore.get(targetFolderId)
          folderRequest.onsuccess = () => resolve(folderRequest.result)
          folderRequest.onerror = () => reject(folderRequest.error)
        })
        isSensitive = folder?.folderType === 'SENSITIVE'
      } catch (error) {
        console.warn('Could not check folder type:', error)
      }
    }
    
    // Add local-specific metadata
    const localNote = {
      ...note,
      noteId: note.noteId || 'note-' + crypto.randomUUID(),
      folderId: targetFolderId,
      isLocal: true,
      syncStatus: 'local_only',
      createdAt: note.createdAt || new Date().toISOString(),
      lastModified: new Date().toISOString()
    }
    
    // Encrypt content if it's a sensitive note and password is provided
    if (isSensitive && password && localNote.content) {
      console.log('🔐 Encrypting sensitive note content')
      localNote.content = await this.encryptContent(localNote.content, password)
      localNote.isEncrypted = true
    }
    
    return new Promise((resolve, reject) => {
      const request = notesStore.put(localNote)
      request.onsuccess = () => {
        // Track usage asynchronously to avoid transaction conflicts
        setTimeout(() => this.trackUsage('note_saved', localNote), 0)
        console.log('💾 Note saved locally:', localNote.noteId, isSensitive ? '(encrypted)' : '')
        resolve(localNote)
      }
      request.onerror = () => reject(request.error)
    })
  }

  /**
   * Get all notes from local storage
   */
  async getAllNotes() {
    if (!this.db) await this.initialize()

    const transaction = this.db.transaction(['local_notes'], 'readonly')
    const store = transaction.objectStore('local_notes')
    
    return new Promise((resolve, reject) => {
      const request = store.getAll()
      request.onsuccess = () => {
        const notes = request.result.sort((a, b) => 
          new Date(b.lastModified) - new Date(a.lastModified)
        )
        console.log('📋 Loaded', notes.length, 'notes from local storage')
        resolve(notes)
      }
      request.onerror = () => reject(request.error)
    })
  }

  /**
   * Get all notes with decryption for sensitive notes if password provided
   */
  async getAllNotesWithDecryption(password = null) {
    const notes = await this.getAllNotes()
    
    if (!password) return notes
    
    // Decrypt sensitive notes
    const decryptedNotes = []
    for (const note of notes) {
      if (note.isEncrypted && note.content) {
        try {
          console.log('🔓 Decrypting sensitive note:', note.noteId)
          const decryptedContent = await this.decryptContent(note.content, password)
          decryptedNotes.push({
            ...note,
            content: decryptedContent
          })
        } catch (error) {
          console.error('Failed to decrypt note:', note.noteId, error)
          decryptedNotes.push(note)
        }
      } else {
        decryptedNotes.push(note)
      }
    }
    return decryptedNotes
  }

  /**
   * Get note by ID from local storage
   */
  async getNoteById(noteId) {
    if (!this.db) await this.initialize()

    const transaction = this.db.transaction(['local_notes'], 'readonly')
    const store = transaction.objectStore('local_notes')
    
    return new Promise((resolve, reject) => {
      const request = store.get(noteId)
      request.onsuccess = () => {
        const note = request.result
        if (note) {
          console.log('📄 Loaded note from local storage:', noteId)
        }
        resolve(note || null)
      }
      request.onerror = () => reject(request.error)
    })
  }

  /**
   * Update existing note in local storage
   */
  async updateNote(noteId, updates) {
    if (!this.db) await this.initialize()

    const existingNote = await this.getNoteById(noteId)
    if (!existingNote) {
      throw new Error('Note not found: ' + noteId)
    }

    const updatedNote = {
      ...existingNote,
      ...updates,
      lastModified: new Date().toISOString(),
      syncStatus: 'local_only'
    }

    return this.saveNote(updatedNote)
  }

  /**
   * Update note with cloud file ID
   */
  async updateNoteCloudId(noteId, cloudFileId) {
    if (!this.db) await this.initialize()

    const transaction = this.db.transaction(['local_notes'], 'readwrite')
    const store = transaction.objectStore('local_notes')
    
    return new Promise((resolve, reject) => {
      const getRequest = store.get(noteId)
      getRequest.onsuccess = () => {
        const note = getRequest.result
        if (note) {
          note.cloudFileId = cloudFileId
          note.lastModified = new Date().toISOString()
          
          const putRequest = store.put(note)
          putRequest.onsuccess = () => {
            console.log('☁️ Updated cloud file ID for note:', noteId, cloudFileId)
            resolve(note)
          }
          putRequest.onerror = () => reject(putRequest.error)
        } else {
          reject(new Error('Note not found'))
        }
      }
      getRequest.onerror = () => reject(getRequest.error)
    })
  }

  /**
   * Delete note from local storage
   */
  async deleteNote(noteId) {
    if (!this.db) await this.initialize()

    const transaction = this.db.transaction(['local_notes'], 'readwrite')
    const store = transaction.objectStore('local_notes')
    
    return new Promise((resolve, reject) => {
      const request = store.delete(noteId)
      request.onsuccess = () => {
        this.trackUsage('note_deleted', { noteId })
        console.log('🗑️ Note deleted from local storage:', noteId)
        resolve(true)
      }
      request.onerror = () => reject(request.error)
    })
  }

  /**
   * Search notes in local storage
   */
  async searchNotes(searchTerm) {
    const allNotes = await this.getAllNotes()
    
    if (!searchTerm || searchTerm.trim() === '') {
      return allNotes
    }

    const term = searchTerm.toLowerCase()
    return allNotes.filter(note => 
      note.title?.toLowerCase().includes(term) ||
      note.content?.toLowerCase().includes(term)
    )
  }

  /**
   * Save folder to local storage
   */
  async saveFolder(folder) {
    if (!this.db) await this.initialize()

    const transaction = this.db.transaction(['local_folders'], 'readwrite')
    const store = transaction.objectStore('local_folders')
    
    const localFolder = {
      ...folder,
      folderId: folder.folderId || 'folder-' + crypto.randomUUID(),
      folderType: folder.folderType || 'CUSTOM', // Ensure custom folders have the correct type
      isLocal: true,
      createdAt: folder.createdAt || new Date().toISOString()
    }
    
    return new Promise((resolve, reject) => {
      const request = store.put(localFolder)
      request.onsuccess = () => {
        console.log('📁 Folder saved locally:', localFolder.folderId)
        resolve(localFolder)
      }
      request.onerror = () => reject(request.error)
    })
  }

  /**
   * Get all folders from local storage
   */
  async getAllFolders() {
    if (!this.db) await this.initialize()

    const transaction = this.db.transaction(['local_folders'], 'readonly')
    const store = transaction.objectStore('local_folders')
    
    return new Promise((resolve, reject) => {
      const request = store.getAll()
      request.onsuccess = () => {
        const folders = request.result.sort((a, b) => a.name.localeCompare(b.name))
        resolve(folders)
      }
      request.onerror = () => reject(request.error)
    })
  }

  /**
   * Delete folder from local storage
   */
  async deleteFolder(folderId) {
    if (!this.db) await this.initialize()

    const transaction = this.db.transaction(['local_folders'], 'readwrite')
    const store = transaction.objectStore('local_folders')
    
    return new Promise((resolve, reject) => {
      const request = store.delete(folderId)
      request.onsuccess = () => {
        console.log('🗑️ Folder deleted locally:', folderId)
        resolve(true)
      }
      request.onerror = () => reject(request.error)
    })
  }

  /**
   * Calculate local storage usage
   */
  async getStorageUsage() {
    try {
      const notes = await this.getAllNotes()
      const folders = await this.getAllFolders()
      
      let totalBytes = 0
      let notesCount = notes.length
      
      // Estimate storage size
      notes.forEach(note => {
        totalBytes += this.estimateNoteSize(note)
      })
      
      folders.forEach(folder => {
        totalBytes += this.estimateFolderSize(folder)
      })
      
      const usage = {
        totalBytes,
        notesCount,
        foldersCount: folders.length,
        formatted: this.formatBytes(totalBytes),
        sessionId: this.sessionId,
        lastCalculated: new Date().toISOString()
      }
      
      // Update usage tracking
      await this.updateUsageStats(usage)
      
      return usage
    } catch (error) {
      console.error('❌ Failed to calculate storage usage:', error)
      return {
        totalBytes: 0,
        notesCount: 0,
        foldersCount: 0,
        formatted: '0 bytes',
        error: error.message
      }
    }
  }

  /**
   * Track usage statistics
   */
  async trackUsage(action, data) {
    try {
      if (!this.db) return

      // Get today's actions first
      const todayActions = await this.getTodayActions() || []
      
      // Create a new transaction for writing
      const transaction = this.db.transaction(['local_usage'], 'readwrite')
      const store = transaction.objectStore('local_usage')
      
      const today = new Date().toISOString().split('T')[0]
      const usage = {
        date: today,
        actions: todayActions,
        lastAction: action,
        timestamp: new Date().toISOString()
      }
      
      usage.actions.push({
        action,
        timestamp: new Date().toISOString(),
        data: { noteId: data?.noteId, size: this.estimateNoteSize(data) }
      })
      
      // Wrap the put operation in a promise
      return new Promise((resolve, reject) => {
        const request = store.put(usage)
        request.onsuccess = () => resolve()
        request.onerror = () => reject(request.error)
      })
    } catch (error) {
      console.warn('⚠️ Failed to track usage:', error)
    }
  }

  /**
   * Get today's actions
   */
  async getTodayActions() {
    try {
      const transaction = this.db.transaction(['local_usage'], 'readonly')
      const store = transaction.objectStore('local_usage')
      const today = new Date().toISOString().split('T')[0]
      
      return new Promise((resolve) => {
        const request = store.get(today)
        request.onsuccess = () => {
          resolve(request.result?.actions || [])
        }
        request.onerror = () => resolve([])
      })
    } catch (error) {
      return []
    }
  }

  /**
   * Update usage statistics
   */
  async updateUsageStats(usage) {
    // Skip usage tracking in local mode to avoid authentication errors
    // Local mode is designed to work completely offline
    return
    
    try {
      // Send usage to backend for tracking (optional - fail silently if no auth)
      if (this.sessionId) {
        const response = await fetch('/api/local/session/' + this.sessionId + '/usage', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            storageBytes: usage.totalBytes,
            notesCount: usage.notesCount
          })
        })
        
        // Only log success or non-auth errors
        if (response.ok) {
          console.log('📊 Usage stats updated successfully')
        } else if (response.status !== 401 && response.status !== 403) {
          console.warn('⚠️ Failed to update usage stats:', response.statusText)
        }
        // Silently ignore 401/403 auth errors in local mode
      }
    } catch (error) {
      // Fail silently - usage tracking is not critical for local mode functionality
      console.debug('⚠️ Usage tracking unavailable:', error.message)
    }
  }

  /**
   * Export all local data for sync
   */
  async exportForSync() {
    try {
      const notes = await this.getAllNotes()
      const folders = await this.getAllFolders()
      const usage = await this.getStorageUsage()
      
      return {
        sessionId: this.sessionId,
        notes,
        folders,
        usage,
        exportedAt: new Date().toISOString()
      }
    } catch (error) {
      console.error('❌ Failed to export local data:', error)
      throw error
    }
  }

  /**
   * Convert ArrayBuffer to base64 in chunks to avoid stack overflow
   * @private
   */
  async arrayBufferToBase64(buffer) {
    return new Promise((resolve) => {
      const bytes = new Uint8Array(buffer)
      const chunkSize = 8192 // Process 8KB at a time
      let binaryString = ''
      
      for (let i = 0; i < bytes.length; i += chunkSize) {
        const chunk = bytes.slice(i, i + chunkSize)
        binaryString += String.fromCharCode.apply(null, chunk)
      }
      
      resolve(btoa(binaryString))
    })
  }

  /**
   * Save attachment to local storage
   */
  async saveAttachment(file) {
    if (!this.db) await this.initialize()

    try {
      // Generate unique ID for the attachment
      const attachmentId = 'attachment-' + crypto.randomUUID()
      
      // Convert file to base64 for storage (chunked to avoid stack overflow)
      const fileBuffer = await file.arrayBuffer()
      const base64Data = await this.arrayBufferToBase64(fileBuffer)
      
      // Create attachment object
      const attachment = {
        id: attachmentId,
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        data: base64Data,
        uploadedAt: new Date().toISOString()
      }
      
      const transaction = this.db.transaction(['local_attachments'], 'readwrite')
      const store = transaction.objectStore('local_attachments')
      
      return new Promise((resolve, reject) => {
        const request = store.add(attachment)
        request.onsuccess = () => {
          console.log('📎 Attachment saved locally:', attachmentId, file.name)
          resolve({
            id: attachmentId,
            fileName: file.name,
            fileType: file.type,
            fileSize: file.size
          })
        }
        request.onerror = () => reject(request.error)
      })
    } catch (error) {
      console.error('❌ Failed to save attachment:', error)
      throw error
    }
  }

  /**
   * Save cloud attachment to local cache (for offline access)
   */
  async saveCloudAttachment(file, cloudFileId) {
    if (!this.db) await this.initialize()

    try {
      // Convert file to base64 for storage (chunked to avoid stack overflow)
      const fileBuffer = await file.arrayBuffer()
      const base64Data = await this.arrayBufferToBase64(fileBuffer)
      
      // Create cloud attachment object
      const attachment = {
        id: file.name, // Use filename as the key for easier lookup
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        data: base64Data,
        cloudFileId: cloudFileId,
        cachedAt: new Date().toISOString(),
        cacheType: 'cloud'
      }
      
      const transaction = this.db.transaction(['local_attachments'], 'readwrite')
      const store = transaction.objectStore('local_attachments')
      
      return new Promise((resolve, reject) => {
        const request = store.add(attachment)
        request.onsuccess = () => {
          console.log('📎 Cloud attachment cached locally:', file.name, 'with cloud ID:', cloudFileId)
          resolve({
            success: true,
            id: cloudFileId,
            fileName: file.name,
            fileType: file.type,
            fileSize: file.size,
            cloudFileId: cloudFileId
          })
        }
        request.onerror = () => reject(request.error)
      })
    } catch (error) {
      console.error('❌ Failed to cache cloud attachment:', error)
      throw error
    }
  }

  /**
   * Get attachment from local storage
   */
  async getAttachment(attachmentId) {
    if (!this.db) await this.initialize()

    const transaction = this.db.transaction(['local_attachments'], 'readonly')
    const store = transaction.objectStore('local_attachments')
    
    return new Promise((resolve, reject) => {
      const request = store.get(attachmentId)
      request.onsuccess = () => {
        if (request.result) {
          console.log('📎 Retrieved attachment:', attachmentId)
          resolve(request.result)
        } else {
          reject(new Error('Attachment not found'))
        }
      }
      request.onerror = () => reject(request.error)
    })
  }

  /**
   * Delete attachment from local storage
   */
  async deleteAttachment(attachmentId) {
    if (!this.db) await this.initialize()

    const transaction = this.db.transaction(['local_attachments'], 'readwrite')
    const store = transaction.objectStore('local_attachments')
    
    return new Promise((resolve, reject) => {
      const request = store.delete(attachmentId)
      request.onsuccess = () => {
        console.log('🗑️ Attachment deleted:', attachmentId)
        resolve(true)
      }
      request.onerror = () => reject(request.error)
    })
  }

  /**
   * Get all attachments from local storage
   */
  async getAllAttachments() {
    if (!this.db) await this.initialize()

    const transaction = this.db.transaction(['local_attachments'], 'readonly')
    const store = transaction.objectStore('local_attachments')
    
    return new Promise((resolve, reject) => {
      const request = store.getAll()
      request.onsuccess = () => {
        console.log('📎 Retrieved all attachments:', request.result.length)
        resolve(request.result || [])
      }
      request.onerror = () => reject(request.error)
    })
  }

  /**
   * Save image attachment as file to local storage
   */
  async saveImageAttachment(file) {
    if (!this.db) await this.initialize()

    try {
      // Generate unique ID for the attachment
      const attachmentId = 'img-' + crypto.randomUUID()
      
      // Create file path for the attachment
      const fileExtension = file.name.split('.').pop() || 'jpg'
      const fileName = `${attachmentId}.${fileExtension}`
      const filePath = `attachments/images/${fileName}`
      
      // Store file in IndexedDB as ArrayBuffer (more reliable than Blob)
      const fileBuffer = await file.arrayBuffer()
      
      console.log('📎 Storing file buffer:', {
        size: fileBuffer.byteLength,
        type: file.type,
        fileName: file.name
      })
      
      // Create attachment object with file reference instead of base64 data
      const attachment = {
        id: attachmentId,
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        filePath: filePath,
        isImage: true,
        uploadedAt: new Date().toISOString()
      }
      
      // Store the file blob separately
      const fileTransaction = this.db.transaction(['local_files'], 'readwrite')
      const fileStore = fileTransaction.objectStore('local_files')
      
      const filePromise = new Promise((resolve, reject) => {
        const fileRequest = fileStore.add({ id: attachmentId, buffer: fileBuffer, type: file.type, path: filePath })
        fileRequest.onsuccess = () => resolve()
        fileRequest.onerror = () => reject(fileRequest.error)
      })
      
      // Store the attachment metadata
      const attachmentTransaction = this.db.transaction(['local_attachments'], 'readwrite')
      const attachmentStore = attachmentTransaction.objectStore('local_attachments')
      
      const attachmentPromise = new Promise((resolve, reject) => {
        const attachmentRequest = attachmentStore.add(attachment)
        attachmentRequest.onsuccess = () => resolve()
        attachmentRequest.onerror = () => reject(attachmentRequest.error)
      })
      
      await Promise.all([filePromise, attachmentPromise])
      
      console.log('📎 Image attachment saved locally:', attachmentId, file.name)
      return {
        id: attachmentId,
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        filePath: filePath
      }
    } catch (error) {
      console.error('❌ Failed to save image attachment:', error)
      throw error
    }
  }

  /**
   * Get image attachment from local storage
   */
  async getImageAttachment(attachmentId) {
    if (!this.db) await this.initialize()

    try {
      // Get attachment metadata
      const attachmentTransaction = this.db.transaction(['local_attachments'], 'readonly')
      const attachmentStore = attachmentTransaction.objectStore('local_attachments')
      
      const attachment = await new Promise((resolve, reject) => {
        const request = attachmentStore.get(attachmentId)
        request.onsuccess = () => {
          if (request.result) {
            resolve(request.result)
          } else {
            reject(new Error('Attachment not found'))
          }
        }
        request.onerror = () => reject(request.error)
      })
      
      // Get file blob
      const fileTransaction = this.db.transaction(['local_files'], 'readonly')
      const fileStore = fileTransaction.objectStore('local_files')
      
      const fileData = await new Promise((resolve, reject) => {
        const request = fileStore.get(attachmentId)
        request.onsuccess = () => {
          if (request.result) {
            resolve(request.result)
          } else {
            reject(new Error('File not found'))
          }
        }
        request.onerror = () => reject(request.error)
      })
      
      // Create Blob from ArrayBuffer and then object URL
      const blob = new Blob([fileData.buffer], { type: fileData.type })
      const objectUrl = URL.createObjectURL(blob)
      
      console.log('📎 Retrieved image attachment:', attachmentId)
      console.log('📎 Blob details:', {
        size: blob.size,
        type: blob.type,
        objectUrl: objectUrl
      })
      
      // Test the blob immediately
      const testImg = new Image()
      testImg.onload = () => {
        console.log('✅ Blob test successful - image loads correctly')
      }
      testImg.onerror = (error) => {
        console.error('❌ Blob test failed - image cannot load:', error)
      }
      testImg.src = objectUrl
      
      return {
        ...attachment,
        objectUrl: objectUrl,
        blob: blob
      }
    } catch (error) {
      console.error('❌ Failed to get image attachment:', error)
      throw error
    }
  }

  /**
   * Clear all local data
   */
  async clearAllData() {
    try {
      if (!this.db) await this.initialize()

      const stores = ['local_notes', 'local_folders', 'local_attachments', 'local_files', 'local_metadata', 'local_usage']
      const transaction = this.db.transaction(stores, 'readwrite')
      
      stores.forEach(storeName => {
        const store = transaction.objectStore(storeName)
        store.clear()
      })
      
      localStorage.removeItem('localSessionId')
      
      // Clear frontend sync related localStorage items
      localStorage.removeItem('lastFrontendSync')
      localStorage.removeItem('tempToPermMap')
      localStorage.removeItem('frontendSyncQueue')
      localStorage.removeItem('lastSuccessfulSync')
      
      return new Promise((resolve, reject) => {
        transaction.oncomplete = async () => {
          try {
            console.log('🧹 Local storage cleared')
            
            // Also clear the cloud sync cache to prevent data persistence
            console.log('🧹 Clearing cloud sync cache...')
            const deleteRequest = indexedDB.deleteDatabase('CloudNotesCache')
            
            deleteRequest.onsuccess = () => {
              console.log('🧹 Cloud sync cache cleared')
              console.log('✅ All data cleared completely')
              resolve(true)
            }
            
            deleteRequest.onerror = (error) => {
              console.warn('⚠️ Failed to clear cloud sync cache:', error)
              // Still resolve since local data was cleared
              console.log('✅ Local data cleared (cloud cache may remain)')
              resolve(true)
            }
            
            deleteRequest.onblocked = () => {
              console.warn('⚠️ Cloud sync cache deletion blocked, retrying...')
              // Force close any open connections and retry
              setTimeout(() => {
                const retryRequest = indexedDB.deleteDatabase('CloudNotesCache')
                retryRequest.onsuccess = () => {
                  console.log('🧹 Cloud sync cache cleared on retry')
                  console.log('✅ All data cleared completely')
                  resolve(true)
                }
                retryRequest.onerror = () => {
                  console.warn('⚠️ Cloud sync cache retry failed')
                  console.log('✅ Local data cleared (cloud cache may remain)')
                  resolve(true)
                }
              }, 100)
            }
          } catch (error) {
            console.error('❌ Error during cloud cache clearing:', error)
            // Still resolve since local data was cleared
            resolve(true)
          }
        }
        
        transaction.onerror = () => {
          reject(new Error('Failed to clear local storage'))
        }
      })
    } catch (error) {
      console.error('❌ Failed to clear local data:', error)
      throw error
    }
  }

  // Helper methods

  estimateNoteSize(note) {
    if (!note) return 0
    const content = note.content || ''
    const title = note.title || ''
    // Rough estimation: UTF-8 bytes + metadata overhead
    return (content.length + title.length) * 2 + 500
  }

  estimateFolderSize(folder) {
    if (!folder) return 0
    const name = folder.name || ''
    return name.length * 2 + 200 // Basic folder metadata
  }

  formatBytes(bytes) {
    if (bytes >= 1024 * 1024 * 1024) {
      return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
    } else if (bytes >= 1024 * 1024) {
      return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
    } else if (bytes >= 1024) {
      return `${(bytes / 1024).toFixed(2)} KB`
    } else {
      return `${bytes} bytes`
    }
  }

  /**
   * Get session ID
   */
  getSessionId() {
    return this.sessionId
  }

  /**
   * Store image cache data (base64 image by Google Drive file ID)
   */
  async storeImageCache(fileId, cacheData) {
    try {
      await this.initialize()
      
      const transaction = this.db.transaction(['image_cache'], 'readwrite')
      const store = transaction.objectStore('image_cache')
      
      const dataToStore = {
        fileId: fileId,
        ...cacheData
      }
      
      console.log('🔍 Storing image cache:', {
        fileId,
        hasBase64Data: !!cacheData.base64Data,
        base64DataLength: cacheData.base64Data?.length,
        fileName: cacheData.fileName,
        fileType: cacheData.fileType
      })
      
      // Use a promise-based approach to ensure proper transaction handling
      await new Promise((resolve, reject) => {
        const request = store.put(dataToStore)
        request.onsuccess = () => {
          resolve(request.result)
        }
        request.onerror = () => {
          reject(request.error)
        }
      })
      
      console.log('✅ Image cache stored:', fileId)
    } catch (error) {
      console.error('❌ Failed to store image cache:', error)
      throw error
    }
  }

  /**
   * Get image cache data by Google Drive file ID
   */
  async getImageCache(fileId) {
    try {
      await this.initialize()
      
      const transaction = this.db.transaction(['image_cache'], 'readonly')
      const store = transaction.objectStore('image_cache')
      
      // Use a promise-based approach to ensure proper transaction handling
      const result = await new Promise((resolve, reject) => {
        const request = store.get(fileId)
        request.onsuccess = () => {
          resolve(request.result)
        }
        request.onerror = () => {
          reject(request.error)
        }
      })
      
      if (result) {
        console.log('✅ Image cache retrieved:', fileId, {
          hasBase64Data: !!result.base64Data,
          base64DataLength: result.base64Data?.length,
          fileName: result.fileName,
          fileType: result.fileType,
          keys: Object.keys(result)
        })
        return result
      } else {
        console.log('⚠️ Image cache not found:', fileId)
        return null
      }
    } catch (error) {
      console.error('❌ Failed to get image cache:', error)
      throw error
    }
  }

  /**
   * Clear image cache (for cleanup)
   */
  async clearImageCache() {
    try {
      await this.initialize()
      
      const transaction = this.db.transaction(['image_cache'], 'readwrite')
      const store = transaction.objectStore('image_cache')
      
      await store.clear()
      
      console.log('✅ Image cache cleared')
    } catch (error) {
      console.error('❌ Failed to clear image cache:', error)
      throw error
    }
  }

  /**
   * Get all image cache entries (for orphaned file cleanup)
   */
  async getAllImageCacheEntries() {
    try {
      await this.initialize()
      
      const transaction = this.db.transaction(['image_cache'], 'readonly')
      const store = transaction.objectStore('image_cache')
      
      return new Promise((resolve, reject) => {
        const request = store.getAll()
        request.onsuccess = () => resolve(request.result || [])
        request.onerror = () => reject(request.error)
      })
    } catch (error) {
      console.error('❌ Failed to get all image cache entries:', error)
      throw error
    }
  }

  /**
   * Delete specific image cache entry (for orphaned file cleanup)
   */
  async deleteImageCache(fileId) {
    try {
      await this.initialize()
      
      const transaction = this.db.transaction(['image_cache'], 'readwrite')
      const store = transaction.objectStore('image_cache')
      
      // Use a promise-based approach to ensure proper transaction handling
      await new Promise((resolve, reject) => {
        const request = store.delete(fileId)
        request.onsuccess = () => {
          resolve(request.result)
        }
        request.onerror = () => {
          reject(request.error)
        }
      })
      
      console.log('✅ Image cache entry deleted:', fileId)
    } catch (error) {
      console.error('❌ Failed to delete image cache entry:', error)
      throw error
    }
  }
}

// Create singleton instance
const localStorageService = new LocalStorageService()

export default localStorageService 